import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { getErrorMessage } from "../lib/api-client";
import type { ComponentReplacementSelection } from "../lib/component-replacement";
import { isOutputAspectRatio } from "../lib/image-constants";
import {
  runCreateDesignFromSelection,
  runReplaceFromSelection,
} from "../lib/selection-actions";
import {
  frameRectToNormalized,
  type NormalizedRect,
  type ObjectFit,
  type PixelRect,
  padNormalizedRect,
  type Size,
  validateSelection,
} from "../lib/selection-geometry";
import { useNaturalImageSize } from "./useNaturalImageSize";
import {
  type MarqueeResult,
  type MarqueeState,
  useSelectionMarquee,
} from "./useSelectionMarquee";

export type SelectionMode = "off" | "selecting" | "pending";

/** Everything fixed the moment a drag completes: the wire-ready crop, where to
 *  anchor the options menu's frame space, and why Create is disabled if it is. */
interface PendingSelection {
  crop: NormalizedRect | null;
  frame: Size;
  validationMessage: string | null;
}

export interface SelectionState {
  mode: SelectionMode;
  isActive: boolean;
  /** Enter selection mode, or leave it if already in it. */
  toggle: () => void;
  exit: () => void;
  marquee: MarqueeState;
  /** Set between a completed drag and menu dismissal; null otherwise. */
  pending: PendingSelection | null;
  natural: Size | null;
  fit: ObjectFit;
  openMenuAt: PixelRect | null;
  /** Dismiss the options menu but stay in select mode, ready to redraw. */
  closeMenu: () => void;
  createNewDesign: () => void;
  isCreating: boolean;
  error: string | null;
  dismissError: () => void;
  /** Open the asset picker for the pending selection. */
  openReplaceModal: () => void;
  closeReplaceModal: () => void;
  isReplaceModalOpen: boolean;
  /** Swap the selected component for the picked asset. */
  replaceWithAsset: (selection: ComponentReplacementSelection) => void;
  isReplacing: boolean;
}

/**
 * Owns the Refine-page selection flow: mode, the drawn rect, its conversion
 * into a wire-ready crop, and the create-a-design action.
 *
 * The server identifies the subject as part of creating the design — there is
 * no confirmation step. A failure leaves the marquee and the options menu in
 * place, so retrying is one click rather than a redraw.
 */
export function useRefineSelection(
  imageId: string | undefined,
  imageUrl: string | undefined,
): SelectionState {
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();

  const [mode, setMode] = useState<SelectionMode>("off");
  const [pending, setPending] = useState<PendingSelection | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Either in-flight stream locks every selection control: streams are not
  // abortable, and each one ends by navigating away or resetting.
  const busy = isCreating || isReplacing;

  const natural = useNaturalImageSize(imageUrl);
  const fit: ObjectFit =
    natural && isOutputAspectRatio(natural.width, natural.height)
      ? "cover"
      : "contain";

  const handleComplete = useCallback(
    ({ rectPx, frame: drawnFrame }: MarqueeResult) => {
      if (!natural) return;
      const normalized = frameRectToNormalized(
        rectPx,
        drawnFrame,
        natural,
        fit,
      );
      if (!normalized) return;

      const validation = validateSelection(normalized, natural);
      // A too-small selection still opens the menu, with Create disabled and
      // the reason shown, so the user can see what they drew and redraw it.
      setPending({
        crop: validation === true ? padNormalizedRect(normalized) : null,
        frame: drawnFrame,
        validationMessage: validation === true ? null : validation,
      });
      setMode("pending");
    },
    [natural, fit],
  );

  const marquee = useSelectionMarquee(handleComplete);
  const { reset } = marquee;

  const clearSelection = useCallback(() => {
    reset();
    setPending(null);
    setIsCreating(false);
    setIsReplacing(false);
    setIsReplaceModalOpen(false);
    setError(null);
  }, [reset]);

  const exit = useCallback(() => {
    clearSelection();
    setMode("off");
  }, [clearSelection]);

  const toggle = useCallback(() => {
    setMode((previous) => {
      if (previous === "off") return "selecting";
      clearSelection();
      return "off";
    });
  }, [clearSelection]);

  const closeMenu = useCallback(() => {
    // Mid-stream there is nothing to dismiss: the stream is not abortable,
    // so letting the menu (or a click outside it) clear the busy state would
    // only hide work that is about to navigate.
    if (busy) return;
    clearSelection();
    setMode("selecting");
  }, [busy, clearSelection]);

  /** Identify what was selected and generate it as a design of its own. */
  const createNewDesign = useCallback(() => {
    if (!(pending?.crop && imageId) || busy) return;
    setMode("pending");
    setIsCreating(true);
    setError(null);

    const fail = (message: string) => {
      // Back to the menu with the marquee intact, so retrying is one click.
      setError(message);
      setIsCreating(false);
    };

    runCreateDesignFromSelection({
      sourceImageId: imageId,
      crop: pending.crop,
      queryClient,
      router,
      navigate,
      onSuccess: exit,
      onFailure: fail,
    }).catch((err) => fail(getErrorMessage(err)));
  }, [pending, imageId, busy, queryClient, router, navigate, exit]);

  /** Show the asset picker for the pending selection. The menu hides while it
   *  is open and reappears if the user backs out, selection intact. */
  const openReplaceModal = useCallback(() => {
    if (!(pending?.crop && imageId) || busy) return;
    setIsReplaceModalOpen(true);
  }, [pending, imageId, busy]);

  const closeReplaceModal = useCallback(() => {
    setIsReplaceModalOpen(false);
  }, []);

  /** Identify what was selected and swap it for the picked asset. */
  const replaceWithAsset = useCallback(
    (selection: ComponentReplacementSelection) => {
      if (!(pending?.crop && imageId) || busy) return;
      setIsReplaceModalOpen(false);
      setIsReplacing(true);
      setError(null);

      const fail = (message: string) => {
        // Back to the menu with the marquee intact, so retrying is one click.
        setError(message);
        setIsReplacing(false);
      };

      runReplaceFromSelection({
        sourceImageId: imageId,
        crop: pending.crop,
        selection,
        queryClient,
        router,
        navigate,
        onSuccess: exit,
        onFailure: fail,
      }).catch((err) => fail(getErrorMessage(err)));
    },
    [pending, imageId, busy, queryClient, router, navigate, exit],
  );

  // Any move to a different design invalidates the current selection: a marquee
  // drawn over the old image means nothing on the new one. Also covers undo and
  // redo, which swap search params without remounting the route.
  const previousImageIdRef = useRef(imageId);
  useEffect(() => {
    if (previousImageIdRef.current === imageId) return;
    previousImageIdRef.current = imageId;
    exit();
  }, [imageId, exit]);

  // Escape cancels selection, but only while this hook owns it and nothing is
  // in flight — aborting mid-creation would hide a stream that still navigates.
  // While the replace modal is open, Escape belongs to the modal (closing it
  // returns to the options menu with the selection intact).
  useEffect(() => {
    if (mode !== "selecting" && mode !== "pending") return;
    if (busy || isReplaceModalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") exit();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mode, busy, isReplaceModalOpen, exit]);

  return {
    mode,
    isActive: mode !== "off",
    toggle,
    exit,
    marquee,
    pending,
    natural,
    fit,
    openMenuAt: mode === "pending" ? marquee.rectPx : null,
    closeMenu,
    createNewDesign,
    isCreating,
    error,
    dismissError: () => setError(null),
    openReplaceModal,
    closeReplaceModal,
    isReplaceModalOpen,
    replaceWithAsset,
    isReplacing,
  };
}
